import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Building2, Quote } from "lucide-react";

const clients = [
  {
    name: "Western Railways",
    sector: "Government / Transportation",
    desc: "Partnered with Western Railways to deliver digital infrastructure solutions improving passenger information systems and internal operations management.",
    color: "#0EA5E9",
  },
  {
    name: "Adani",
    sector: "Conglomerate / Infrastructure",
    desc: "Developed enterprise-grade software solutions and digital marketing campaigns supporting Adani Group's expanding portfolio of businesses.",
    color: "#10B981",
  },
  {
    name: "Godrej",
    sector: "Consumer Goods / Real Estate",
    desc: "Engineered custom web platforms and social media growth strategies to strengthen Godrej's digital brand presence and customer engagement.",
    color: "#3B82F6",
  },
  {
    name: "Sunburn",
    sector: "Entertainment / Events",
    desc: "Built high-traffic event platforms and executed large-scale social media campaigns driving ticket sales and brand visibility for Asia's premier electronic music festival.",
    color: "#F59E0B",
  },
  {
    name: "Break Bounce",
    sector: "Fashion / Lifestyle",
    desc: "Designed and developed e-commerce platforms with integrated marketing automation, boosting online sales and brand awareness for this lifestyle brand.",
    color: "#8B5CF6",
  },
  {
    name: "Arvind Fashion Retails",
    sector: "Fashion / Retail",
    desc: "Delivered end-to-end digital transformation including custom retail management software, omnichannel web platforms, and data-driven marketing campaigns.",
    color: "#00F2FE",
  },
];

export default function OurClients() {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Badge variant="outline" className="mb-4 border-emerald-500/30 text-emerald-400 bg-emerald-500/5 px-3 py-1 text-xs tracking-wider uppercase">
            Trusted By Industry Leaders
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 mb-4">
            Our <span className="gradient-text">Clients</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
            We are proud to partner with leading enterprises and brands across sectors.
          </p>
        </motion.div>

        {/* Client Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client, i) => (
            <motion.div
              key={client.name}
              data-testid={`client-card-${client.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-[#0F172A] border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.15] transition-all"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
            >
              {/* Icon & Name */}
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${client.color}15`, border: `1px solid ${client.color}30` }}
                >
                  <Building2 className="h-6 w-6" style={{ color: client.color }} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-100">{client.name}</h3>
                  <span className="text-xs text-slate-500 font-mono tracking-wider">{client.sector}</span>
                </div>
              </div>

              {/* Description */}
              <p className="text-slate-400 text-sm leading-relaxed">{client.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Testimonial-style trust line */}
        <motion.div
          className="mt-16 bg-[#0F172A] border border-white/[0.06] rounded-xl p-8 sm:p-10 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Quote className="h-8 w-8 text-[#00F2FE]/30 mx-auto mb-4" />
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto font-medium italic">
            "Devkuba Ventures consistently delivers on time and beyond expectations. Their technical depth and strategic thinking have been invaluable to our digital transformation."
          </p>
          <p className="text-slate-500 text-sm mt-4">— Enterprise Client Feedback</p>
        </motion.div>
      </div>
    </div>
  );
}
